import axios from 'axios';
import { getToken, getNewToken } from './Auth.js';
import errorLogger from '../utils/ErrorLogger';

const apiBaseUrl = process.env.REACT_APP_API_ENDPOINT;
const dplBaseUrl = process.env.REACT_APP_DPL_BASE_URL;

// Add request/response interceptors for logging
axios.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => {
    errorLogger.logError(error, 'api-request-interceptor');
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    const duration = Date.now() - response.config.metadata.startTime;
    errorLogger.logApiCall(
      response.config.method?.toUpperCase(),
      response.config.url,
      response.config.data,
      response.data,
      duration
    );
    return response;
  },
  (error) => {
    const duration = error.config?.metadata ? 
      Date.now() - error.config.metadata.startTime : 0;
    
    errorLogger.logApiCall(
      error.config?.method?.toUpperCase(),
      error.config?.url,
      error.config?.data,
      error.response?.data,
      duration,
      error
    );
    return Promise.reject(error);
  }
);

const getValidToken = async () => {
  let token = getToken();

  if (!token) {
    errorLogger.logWarning('No token available, attempting refresh', 'auth');
    const refreshed = await getNewToken();

    if (!refreshed) {
      const error = new Error('No valid token available');
      errorLogger.logError(error, 'auth', { action: 'token-validation' });
      throw error;
    }

    token = getToken();
    errorLogger.logInfo('Token refreshed successfully', 'auth');
  }

  return token;
};

// Helper function for making API requests with token refresh capability
const makeAuthenticatedRequest = async (requestFn) => {
  const startTime = Date.now();
  
  try {
    const token = await getValidToken();
    const result = await requestFn(token);
    
    const duration = Date.now() - startTime;
    errorLogger.logInfo(`API request completed in ${duration}ms`, 'api');
    
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    
    if (error.response && error.response.status === 401) {
      errorLogger.logWarning('Received 401, attempting token refresh', 'api', {
        duration,
        originalError: error.message
      });
      
      try {
        const newToken = await getValidToken();
        const result = await requestFn(newToken);
        
        errorLogger.logInfo('Request succeeded after token refresh', 'api', {
          totalDuration: Date.now() - startTime
        });
        
        return result;
      } catch (retryError) {
        errorLogger.logError(retryError, 'api', {
          context: 'token-refresh-retry',
          originalError: error.message,
          totalDuration: Date.now() - startTime
        });
        throw retryError;
      }
    }

    // Enhanced error logging with more context
    errorLogger.logError(error, 'api', {
      duration,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      url: error.config?.url,
      method: error.config?.method
    });

    throw error;
  }
};

const getUserDetail = async (testid = null) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/user-detail${testid ? '?testid=' + testid : ''}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const getClassList = async (testid = null, strm = null) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/class-list${strm ? '?strm=' + strm : ''}${testid ? '&testid=' + testid : ''}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const getEdnaAccess = async (testid = null) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/edna-access${testid ? '?testid=' + testid : ''}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const getEdnaAccessList = async () => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/edna-access-list?servicePath=Syllabusupload.Admin`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const getDownloadUrl = async (classNumber, strm) => {
  // return makeAuthenticatedRequest(async (token) => {
  const result = await axios({
    method: 'GET',
    url: `${apiBaseUrl}/get-download-url?classNumber=${classNumber}&strm=${strm}`,
    // headers: {
    //   Authorization: `Bearer ${token}`,
    // },
  });
  return result.data;
  // });
};

const removeSyllabus = async (classNumber, strm) => {
  return makeAuthenticatedRequest(async (token) => {
    await axios({
      method: 'DELETE',
      url: `${apiBaseUrl}/remove-syllabi`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: { classNumber, strm },
    });
    return 'Syllabus removed successfully.';
  });
};

const addUser = async (user) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'POST',
      url: `${apiBaseUrl}/manage-users`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: user,
    });
    return result.data;
  });
};

const removeUsers = async (userIds) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'DELETE',
      url: `${apiBaseUrl}/manage-users`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: userIds,
    });
    return result.data;
  });
};

const getUsers = async () => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'GET',
      url: `${apiBaseUrl}/manage-users`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return result.data;
  });
};

const search = async (input, testid = null) => {
  return makeAuthenticatedRequest(async (token) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 7000);

    try {
      const result = await axios({
        method: 'POST',
        url: `${apiBaseUrl}/class-search${testid ? '?testid=' + testid : ''}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        data: input,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return result.data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Timed out while fetching search result');
      }
      throw error;
    }
  });
};

const uploadFile = async (file, meta) => {
  const fileType = file.type;
  const filename = file.name;

  meta.filetype = fileType;
  meta.filename = filename;

  errorLogger.logUserAction('upload-file-started', {
    filename,
    fileType,
    fileSize: file.size,
    classNumber: meta.classNumber,
    strm: meta.strm
  });

  return makeAuthenticatedRequest(async (token) => {
    try {
      // Convert file to ArrayBuffer
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
      });

      errorLogger.logInfo('File read successfully', 'upload', {
        filename,
        dataSize: data.byteLength
      });

      // Get s3 signed upload URL
      const options = {
        method: 'GET',
        url: `${apiBaseUrl}/get-upload-url?strm=${meta.strm}&classNumber=${meta.classNumber}`,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': fileType,
        },
        data: data,
      };

      const result = await axios(options);
      const uploadUrl = result.data.uploadURL;

      errorLogger.logInfo('Upload URL obtained', 'upload', {
        classNumber: meta.classNumber,
        hasUploadUrl: !!uploadUrl
      });

      // Upload to S3
      const s3Put = {
        method: 'PUT',
        url: uploadUrl,
        headers: {
          'Content-Type': fileType,
        },
        data: data,
      };

      await axios(s3Put);

      errorLogger.logInfo('File uploaded to S3 successfully', 'upload', {
        filename,
        classNumber: meta.classNumber
      });

      const publishStatus = await pollStatus(meta, 1000);

      if (publishStatus.status === 'READY') {
        errorLogger.logUserAction('upload-file-completed', {
          filename,
          classNumber: meta.classNumber,
          status: 'success'
        });
        
        return `Syllabus for class ${meta.classNumber} uploaded successfully!`;
      } else {
        const error = new Error(
          `An error occurred while processing the syllabus file for class ${meta.classNumber}. Please try again or check back later.`
        );
        
        errorLogger.logError(error, 'upload', {
          filename,
          classNumber: meta.classNumber,
          publishStatus
        });
        
        throw error;
      }
    } catch (error) {
      // Provide specific error messages based on error codes
      let errorMessage = error.message;
      let errorContext = {
        filename,
        classNumber: meta.classNumber,
        fileSize: file.size,
        fileType
      };

      if (error.code === 'INVALID_PDF') {
        errorMessage = 'The uploaded file is not a valid PDF. Please ensure the file is a properly formatted PDF document.';
        errorContext.errorType = 'invalid_pdf';
      } else if (error.response?.status === 413) {
        errorMessage = 'The file is too large. Please ensure the PDF is under the size limit.';
        errorContext.errorType = 'file_too_large';
      } else if (error.response?.status === 403) {
        // Enhanced 403 error handling with specific guidance
        let specificMessage = 'You do not have permission to upload files for this class.';
        let suggestions = [];
        
        try {
          const errorData = error.response?.data;
          if (typeof errorData === 'string') {
            specificMessage = errorData;
          } else if (errorData?.message) {
            specificMessage = errorData.message;
          }
          
          // Add specific suggestions based on the error
          suggestions = [
            'Verify you are listed as an instructor for this class',
            'Check that you are using the correct term/semester',
            'Contact support if you believe this is an error',
            'Try refreshing the page and logging in again'
          ];
          
          // Log additional context for 403 errors
          errorLogger.logError(new Error('403 Authorization Error Details'), 'upload', {
            filename,
            classNumber: meta.classNumber,
            strm: meta.strm,
            errorResponse: errorData,
            userGuidance: suggestions,
            diagnosticEndpoint: `/diagnose-auth?classNumber=${meta.classNumber}&strm=${meta.strm}`
          });
          
        } catch (e) {
          // Use default message if parsing fails
          suggestions = [
            'Verify you are listed as an instructor for this class',
            'Contact support for assistance'
          ];
        }
        
        errorMessage = `${specificMessage}\n\nSuggestions:\n${suggestions.map(s => `• ${s}`).join('\n')}`;
        errorContext.errorType = 'permission_denied';
        errorContext.httpStatus = 403;
        errorContext.responseData = error.response?.data;
        errorContext.suggestions = suggestions;
      } else if (error.name === 'AbortError' || error.message.includes('timeout')) {
        errorMessage = 'The upload timed out. Please check your internet connection and try again.';
        errorContext.errorType = 'timeout';
      }

      const enhancedError = new Error(`Failed to upload syllabus for class ${meta.classNumber}: ${errorMessage}`);
      enhancedError.code = error.code;
      enhancedError.originalError = error;

      errorLogger.logError(enhancedError, 'upload', errorContext);
      errorLogger.logUserAction('upload-file-failed', {
        ...errorContext,
        errorMessage
      });

      throw enhancedError;
    }
  });
};

const uploadUrl = async (url, meta) => {
  meta.url = url;

  return makeAuthenticatedRequest(async (token) => {
    const options = {
      method: 'POST',
      url: `${apiBaseUrl}/upload-from-url?strm=${meta.strm}&classNumber=${meta.classNumber}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: meta,
    };

    const result = await axios(options);
    return 'Syllabus updated successfully!';
  });
};

const getTerms = async () => {
  const now = new Date();
  const halfRange = 31540000 * 1000; // 12 months
  const pastLimit = new Date(now.getTime() - halfRange).getTime();
  const futureLimit = new Date(now.getTime() + halfRange).getTime();

  const strmsAll = await axios({
    method: 'GET',
    url: `${dplBaseUrl}/codeset/terms`,
  });

  const strmsArr = Object.values(strmsAll.data);
  let strms = strmsArr.filter(
    (strm) =>
      strm.latestTermEndDate >= pastLimit &&
      strm.earliestTermBeginDate <= futureLimit
  );

  let strmsSimple = strms.map((strm) => ({
    strm: strm.strm,
    description: strm.description,
  }));
  strmsSimple.sort((a, b) => (a.strm > b.strm ? -1 : 1)); // Descending order

  const current = await axios({
    method: 'GET',
    url: `${dplBaseUrl}/codeset/term/current`,
  });

  return {
    termsList: strmsSimple,
    workingTerm: current.data.strm,
  };
};

const syllabiExist = async (classes) => {
  return makeAuthenticatedRequest(async (token) => {
    const result = await axios({
      method: 'POST',
      url: `${apiBaseUrl}/syllabi-exist`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: classes,
    });
    return result.data;
  });
};

const pollStatus = (params, interval, maxTries) => {
  let attempts = 0;
  const startTime = Date.now();

  errorLogger.logInfo('Starting status polling', 'upload', {
    classNumber: params.classNumber,
    strm: params.strm,
    interval,
    maxTries
  });

  return new Promise((resolve, reject) => {
    const executePoll = async () => {
      try {
        const result = await getPublishStatus(params);
        attempts++;
        const elapsed = Date.now() - startTime;

        errorLogger.logInfo('Poll status check', 'upload', {
          attempt: attempts,
          status: result.status,
          elapsed,
          classNumber: params.classNumber
        });

        if (result.status === 'READY') {
          errorLogger.logInfo('Processing completed successfully', 'upload', {
            attempts,
            totalTime: elapsed,
            classNumber: params.classNumber
          });
          resolve(result);
        } else if (result.status === 'ERROR') {
          const error = new Error(
            result.errorMessage || 'An error occurred while processing the file'
          );
          error.code = result.errorCode;
          
          errorLogger.logError(error, 'upload', {
            attempts,
            totalTime: elapsed,
            classNumber: params.classNumber,
            errorCode: result.errorCode,
            errorMessage: result.errorMessage
          });
          
          reject(error);
        } else if (maxTries && maxTries === attempts) {
          const timeoutError = new Error('Processing timed out. Please try again later.');
          
          errorLogger.logError(timeoutError, 'upload', {
            attempts,
            totalTime: elapsed,
            classNumber: params.classNumber,
            lastStatus: result.status
          });
          
          reject(timeoutError);
        } else {
          setTimeout(executePoll, interval);
        }
      } catch (error) {
        const elapsed = Date.now() - startTime;
        
        errorLogger.logError(error, 'upload', {
          context: 'poll-status-error',
          attempts,
          totalTime: elapsed,
          classNumber: params.classNumber
        });
        
        reject(error);
      }
    };

    executePoll();
  });
};

const getPublishStatus = async ({ strm, classNumber }) => {
  return makeAuthenticatedRequest(async (token) => {
    const statusOptions = {
      method: 'GET',
      url: `${apiBaseUrl}/get-publish-status?strm=${strm}&classNumber=${classNumber}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const publishStatusResp = await axios(statusOptions);
    // Response contains full DynamoDB item with status, errorCode, errorMessage, etc.
    return publishStatusResp.data;
  });
};

export {
  search,
  getDownloadUrl,
  uploadFile,
  uploadUrl,
  getUsers,
  addUser,
  removeUsers,
  getUserDetail,
  getClassList,
  getEdnaAccess,
  getEdnaAccessList,
  removeSyllabus,
  getTerms,
  syllabiExist,
};
